import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { IndexQueryParams, IndexResponse } from '../Interfaces/api/users.interface';
import { SortDirection } from '@angular/material/sort';
import { UsersService } from '../Services/users.service';
import { DEFAULT_ORDER_BY, DEFAULT_ORDER_DIRECTION, DEFAULT_PAGE_SIZE } from '../Consts/list-users.const';
import { User } from '../Models/avyna/user.model';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';

@Injectable()
export class ListUsersFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _usersService = inject(UsersService);

    private readonly _searchTerm = signal('');
    private readonly _orderBy = signal(DEFAULT_ORDER_BY);
    private readonly _order = signal<SortDirection>(DEFAULT_ORDER_DIRECTION);
    private readonly _pageIndex = signal(0);

    private readonly _baseResource = httpResource<IndexResponse>(() => {
        const queryParams: IndexQueryParams = {
            search: this._searchTerm(),
            orderBy: this._orderBy(),
            order: this._order() as 'asc' | 'desc',
            pageSize: DEFAULT_PAGE_SIZE,
            page: this._pageIndex(),
        };
        return this._usersService.index(queryParams);
    });

    // ACTIONS
    public setSearchTerm(term: string) {
        this._searchTerm.set(term ?? '');
        this._pageIndex.set(0);
    }

    public setOrderBy(orderBy: string) {
        this._orderBy.set(orderBy ?? DEFAULT_ORDER_BY);
    }

    public setOrder(order: SortDirection) {
        this._order.set(order ?? DEFAULT_ORDER_DIRECTION);
    }

    public setSort(orderBy: string, order: SortDirection) {
        this.setOrderBy(orderBy);
        this.setOrder(order);
        this._pageIndex.set(0);
    }

    public setPage(pageIndex: number) {
        this._pageIndex.set(Math.max(0, pageIndex));
    }

    public reload() {
        this._baseResource.reload();
    }

    // PUBLIC READ MODELS
    readonly users = computed<User[]>(() => {
        return this._baseResource.hasValue()
            ? (this._resourceState.valueOf(this._baseResource)?.data.data.map((u) => new User(u)) ?? [])
            : [];
    });
    readonly count = computed<number>(() => this._baseResource.hasValue() ? this._resourceState.valueOf(this._baseResource)?.data.count ?? 0 : 0);
    readonly pageIndex = computed<number>(() => this._pageIndex());
    readonly pageSize = computed<number>(() => DEFAULT_PAGE_SIZE);
    readonly searchTerm = computed<string>(() => this._searchTerm());
    readonly orderBy = computed<string>(() => this._orderBy());
    readonly order = computed<SortDirection>(() => this._order() as SortDirection);

    readonly isLoading = computed(() => this._baseResource.isLoading());
    readonly error = computed(() => this._baseResource.error());
    readonly status = computed(() => this._baseResource.status());
}